import React, { useState } from 'react';
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  styled,
  Typography,
} from '@worthy-npm/worthy-common-ui-components';
import { SchedulingType } from '../../types/scheduling';

const schedulingTypeLabels: Record<SchedulingType, { label: string; description: string }> = {
  [SchedulingType.PICKUP]: {
    label: 'Schedule a FedEx Pickup',
    description: 'FedEx will pick up your shipment at your front door.',
  },
  [SchedulingType.DROPOFF]: {
    label: 'Drop-Off at FedEx',
    description: 'Drop your item off at any staffed FedEx location near you.',
  },
};

const StyledFormControlLabel = styled(FormControlLabel)<{ mostPopular?: boolean }>(
  ({ theme, checked, mostPopular }) => ({
    outline: `2px solid ${checked ? theme.palette.tertiary.main : theme.palette.divider}`,
    borderRadius: 4,
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: theme.spacing(2),
    gap: theme.spacing(1.5),
    height: '100%',
    margin: 0,
    ...(mostPopular && {
      backgroundImage: 'url(/images/mostPopular.svg)',
      backgroundPosition: 'calc(100% + 5px) calc(0% - 3px)',
      backgroundRepeat: 'no-repeat',
    }),

    '& .MuiFormControlLabel-label ': {
      color: theme.palette.text.primary,
    },
  }),
);

const StyledRadioGroup = styled(RadioGroup)(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing(2),
  flexWrap: 'nowrap',
}));

const StyledRadio = styled(Radio)(({ theme }) => ({
  color: theme.palette.divider,

  padding: 0,
  '&.Mui-checked': {
    color: theme.palette.highlight.main,
  },
}));

function MethodSelection({
  method,
  setMethod,
}: {
  method: SchedulingType;
  setMethod: (value: SchedulingType) => void;
}) {
  const mostPopular = SchedulingType.PICKUP;

  return (
    <FormControl sx={{ flexDirection: 'row' }}>
      <StyledRadioGroup
        row
        aria-labelledby="shipping-type"
        name="shipping-type"
        value={method}
        onChange={(e) => setMethod(e.target.value as SchedulingType)}
      >
        {Object.values(SchedulingType).map((type) => (
          <StyledFormControlLabel
            key={type}
            value={type}
            checked={method === type}
            mostPopular={mostPopular === type}
            control={<StyledRadio size="medium" />}
            label={(
              <Stack gap={2}>
                <Typography variant="button">{schedulingTypeLabels[type].label}</Typography>
                <Typography>{schedulingTypeLabels[type].description}</Typography>
              </Stack>
            )}
            labelPlacement="bottom"
          />
        ))}
      </StyledRadioGroup>
    </FormControl>
  );
}

export default MethodSelection;
